import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Shield, Building2, Briefcase, UserCheck, Edit3, Save, X } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import EavAttributeField from '../components/EavAttributeField';
import { findFirstEavError } from '../utils/attributeValidation';

const ProfileView = () => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({ email: '', is_super_admin: 0, attributes: {} });
  const [formData, setFormData] = useState({});
  const [attrConfig, setAttrConfig] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [departmentName, setDepartmentName] = useState('');
  const [positionName, setPositionName] = useState('');
  const [supervisorName, setSupervisorName] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [userRes, configRes] = await Promise.all([
          fetch(`/api/employees/current`),
          fetch(`/api/employees/form-config?attribute_set_id=3`),
        ]);

        const userJson = await userRes.json();
        const configJson = await configRes.json();

        if (userJson.status === 'success') {
          const userWithLanguage = { ...userJson.data, language: lang };
          setUserData(userWithLanguage);
          setFormData(userWithLanguage);

          // 加载部门、职位、直属上级信息
          const employeeId = userWithLanguage.entity_id;
          if (employeeId) {
            const posRes = await fetch(`/api/employees/positions?employee_id=${employeeId}`);
            const posJson = await posRes.json();
            if (posJson.status === 'success' && posJson.data && posJson.data.length > 0) {
              setDepartmentName(posJson.data[0].department_name || '');
              setPositionName(posJson.data[0].position_name || '');
            }

            if (userWithLanguage.supervisor_id) {
              const supRes = await fetch(`/api/employees/${userWithLanguage.supervisor_id}`);
              const supJson = await supRes.json();
              if (supJson.status === 'success' && supJson.data) {
                const sup = supJson.data;
                const firstName = sup.attributes?.first_name || '';
                const lastName = sup.attributes?.last_name || '';
                setSupervisorName(`${sup.entity_id} - ${firstName}${lastName}`);
              }
            }
          }
        }

        if (configJson.status === 'success' && configJson.data) {
          setAttrConfig(configJson.data);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [lang]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // 前端 EAV 必填验证
    const firstError = findFirstEavError(attrConfig, formData.attributes || {}, t);
    if (firstError) {
      notifyError(firstError);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/employees/update-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notifySuccess(t('profile.update_success'));
        setUserData(formData);
        setIsEditing(false);
      } else {
        notifyError(t('profile.update_failed') + ': ' + data.message);
      }
    } catch (err) {
      notifyError(t('profile.network_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttributeChange = (code, val) => {
    setFormData(prev => ({ ...prev, attributes: { ...prev.attributes, [code]: val } }));
  };

  if (loading) return <div className="p-10 text-center">{t('profile.loading')}</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/profile')}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title={t('common.back')}
          >
            <X size={20} />
          </button>
          <h1 className="text-2xl font-bold">{t('profile.view_profile')}</h1>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm text-sm"
          >
            <Edit3 size={16} />
            {t('profile.edit_profile')}
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
        {/* Avatar + Email Header */}
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md shrink-0">
            {userData.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white truncate">{userData.email}</h2>
            <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              userData.is_super_admin
                ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
            }`}>
              {userData.is_super_admin ? t('profile.super_admin') : t('profile.normal_employee')}
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave}>
          {/* Email */}
          <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800">
            <Mail size={16} className="text-slate-400 shrink-0" />
            <label className="w-28 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0">{t('profile.email')}</label>
            <input
              type="email"
              className={`flex-1 px-3 py-2 border rounded-lg outline-none transition-all text-sm ${
                isEditing
                  ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900 bg-white dark:bg-slate-800 text-slate-900 dark:text-white'
                  : 'border-transparent bg-transparent text-slate-700 dark:text-slate-300 cursor-default'
              }`}
              value={formData.email || ''}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              disabled={!isEditing}
              required
            />
          </div>

          {/* Admin Status */}
          <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800">
            <Shield size={16} className="text-slate-400 shrink-0" />
            <label className="w-28 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0">{t('profile.admin_status')}</label>
            <div className="flex-1 text-sm text-slate-600 dark:text-slate-400">
              {userData.is_super_admin ? t('profile.super_admin') : t('profile.normal_employee')}
            </div>
          </div>

          {/* 所属部门 */}
          <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800">
            <Building2 size={16} className="text-slate-400 shrink-0" />
            <label className="w-28 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0">{t('profile.department')}</label>
            <div className="flex-1 text-sm text-slate-600 dark:text-slate-400">
              {departmentName || t('common.not_assigned')}
            </div>
          </div>

          {/* 职位 */}
          <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800">
            <Briefcase size={16} className="text-slate-400 shrink-0" />
            <label className="w-28 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0">{t('profile.position')}</label>
            <div className="flex-1 text-sm text-slate-600 dark:text-slate-400">
              {positionName || t('common.not_assigned')}
            </div>
          </div>

          {/* 直属上级 */}
          <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800">
            <UserCheck size={16} className="text-slate-400 shrink-0" />
            <label className="w-28 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0">{t('profile.supervisor')}</label>
            <div className="flex-1 text-sm text-slate-600 dark:text-slate-400">
              {supervisorName || t('common.not_set')}
            </div>
          </div>

          {/* EAV Attributes */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 gap-y-6">
              {attrConfig.map((attr) => (
                <EavAttributeField
                  key={attr.attribute_id}
                  attr={attr}
                  value={(formData.attributes || {})[attr.attribute_code] || ''}
                  onChange={handleAttributeChange}
                  viewOnly={!isEditing}
                />
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="px-6 py-4 flex justify-end gap-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => { setIsEditing(false); setFormData(userData); }}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm text-sm ${
                  isSubmitting ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  <><Save size={16} />{t('common.save')}</>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProfileView;
